import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createReplyAnimator } from "./reply-animator";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  AssistantError,
  assistantApi,
  streamTurn,
  type AssistantSession,
  type HistoryItem,
} from "@/services/assistant-service";

export function useAssistant() {
  const [params, setParams] = useSearchParams();
  const sid = params.get("session");
  const [session, setSession] = useState<AssistantSession | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState(false);
  const [historyMonths, setHistoryMonths] = useState(6);
  const [inputMaxBytes, setInputMaxBytes] = useState(4096);
  const [searching, setSearching] = useState(false);
  const [draftVersion, setDraftVersion] = useState(0);
  const [composerKey, setComposerKey] = useState(0);
  const [restoreVersion, setRestoreVersion] = useState(0);
  const controller = useRef<AbortController | null>(null);
  const busy = useRef(false);
  const current = useRef(session);
  current.current = session;
  const selectedId = useRef(sid);
  selectedId.current = sid;
  const deleting = useRef(new Set<string>());
  const historyRequest = useRef(0);
  const pending = useRef<{ request_id: string; message: string } | null>(null);
  const createId = useRef(crypto.randomUUID());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);

  const refreshHistory = useCallback(async () => {
    const request = ++historyRequest.current;
    try {
      const list = await assistantApi.history();
      if (mounted.current && request === historyRequest.current) {
        setHistory(list.items);
        setHistoryMonths(list.history_months);
        setInputMaxBytes(list.input_max_bytes);
        setHistoryError(false);
      }
    } catch {
      if (mounted.current && request === historyRequest.current)
        setHistoryError(true);
    }
  }, []);
  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);
  useEffect(() => {
    if (!sid) {
      controller.current?.abort();
      busy.current = false;
      setLoading(false);
      setSearching(false);
      setError(null);
      pending.current = null;
      if (current.current) {
        current.current = null;
        pending.current = null;
        setSession(null);
        setError(null);
        createId.current = crypto.randomUUID();
        setComposerKey((key) => key + 1);
      }
      return;
    }
    if (current.current?.id === sid) return;
    // Browser Back/Forward can switch consultations even while a request is pending.
    controller.current?.abort();
    setSearching(false);
    current.current = null;
    pending.current = null;
    setSession(null);
    setComposerKey((key) => key + 1);
    const abort = new AbortController();
    controller.current = abort;
    setLoading(true);
    busy.current = true;
    setError(null);
    assistantApi
      .get(sid, abort.signal)
      .then((value) => {
        if (abort.signal.aborted) return;
        setSession(value);
        pending.current = value.retry;
        if (value.retry)
          setError(
            "답변이 완료되지 않았어요. 다시 시도하거나 새 메시지를 보내 주세요.",
          );
      })
      .catch(() => {
        if (!abort.signal.aborted)
          setError(
            "상담을 불러오지 못했습니다. 지난 상담에서 다시 선택해 주세요.",
          );
      })
      .finally(() => {
        if (controller.current === abort) controller.current = null;
        if (!abort.signal.aborted) {
          setLoading(false);
          busy.current = false;
        }
      });
    return () => abort.abort();
  }, [sid, restoreVersion]);
  // Reconnect after refresh/network loss while another request still owns the lease.
  useEffect(() => {
    if (!session?.processing || busy.current) return;
    const abort = new AbortController();
    const timer = window.setInterval(() => {
      assistantApi
        .get(session.id, abort.signal)
        .then((s) => {
          if (abort.signal.aborted || current.current?.id !== session.id)
            return;
          setSession(s);
          pending.current = s.retry;
          if (s.retry)
            setError(
              "답변이 완료되지 않았어요. 다시 시도하거나 새 메시지를 보내 주세요.",
            );
          else if (!s.processing) setError(null);
        })
        .catch(() => {
          if (!abort.signal.aborted && current.current?.id === session.id)
            setError("상담 상태를 확인하지 못했습니다.");
        });
    }, 3000);
    return () => {
      abort.abort();
      clearInterval(timer);
    };
  }, [session?.id, session?.processing, loading]);

  const send = async (message: string): Promise<boolean> => {
    if (busy.current || current.current?.processing) return false;
    if (current.current?.state.access_changed) {
      pending.current = null;
      setError("자료의 공개 범위가 변경되었습니다. 새 상담을 시작해 주세요.");
      return false;
    }
    if (new TextEncoder().encode(message.trim()).length > inputMaxBytes) {
      setError(
        `메시지는 ${inputMaxBytes.toLocaleString()}바이트까지 입력할 수 있어요.`,
      );
      return false;
    }
    busy.current = true;
    setLoading(true);
    setError(null);
    const abort = new AbortController();
    controller.current = abort;
    const request = (pending.current?.message === message
      ? pending.current
      : null) ?? {
      request_id: crypto.randomUUID(),
      message,
    };
    pending.current = request;
    let active = current.current;
    let stopReply = () => {};
    try {
      if (!active) {
        active = await assistantApi.create(
          createId.current,
          params.get("agent_id") ?? undefined,
          params.get("case_study_id") ?? undefined,
        );
        if (!mounted.current || abort.signal.aborted) return false;
        current.current = active;
        setSession(active);
        setParams({ session: active.id }, { replace: true });
      }
      let completed: AssistantSession | undefined;
      const base = active.messages;
      // Optimistic display is reconciled with the server's masked, idempotent turn.
      const hasPending = active.retry?.request_id === request.request_id;
      let messages = hasPending
        ? base
        : [
            ...base,
            {
              id: request.request_id,
              role: "user" as const,
              content: message,
              timestamp: new Date().toISOString(),
            },
          ];
      setSession({ ...active, messages });
      const animator = createReplyAnimator(abort.signal, (content) => {
        if (!mounted.current || abort.signal.aborted) return;
        setSession(
          (s) =>
            s && {
              ...s,
              messages: [
                ...messages,
                {
                  id: `${request.request_id}-reply`,
                  role: "assistant",
                  content,
                  timestamp: new Date().toISOString(),
                },
              ],
            },
        );
      });
      stopReply = animator.cancel;
      await streamTurn(active.id, request, abort.signal, (kind, data) => {
        if (abort.signal.aborted || !mounted.current) return;
        if (kind === "accepted") {
          const accepted = data as AssistantSession;
          messages = accepted.messages;
          setSession(accepted);
        }
        if (
          kind === "status" &&
          (data as { status: string }).status === "searching"
        )
          setSearching(true);
        if (kind === "delta") animator.append((data as { text: string }).text);
        if (kind === "done") {
          completed = data as AssistantSession;
        }
      });
      await animator.finish();
      if (!mounted.current || abort.signal.aborted) return false;
      if (completed) {
        // Commit the completed insight and candidate panels together, once.
        setSession(completed);
        current.current = completed;
        pending.current = null;
      }
      setDraftVersion((v) => v + 1);
      void refreshHistory();
      return true;
    } catch (e) {
      stopReply();
      if (!mounted.current || abort.signal.aborted) return false;
      setError(
        e instanceof AssistantError
          ? e.message
          : getApiErrorMessage(
              e,
              "메시지를 전송하지 못했습니다. 연결 상태를 확인하고 다시 시도해 주세요.",
            ),
      );
      if (active) {
        try {
          const saved = await assistantApi.get(active.id, abort.signal);
          if (!abort.signal.aborted) {
            setSession(saved);
            pending.current = saved.state.access_changed ? null : saved.retry;
          }
        } catch {
          /* Keep the original request ID for an idempotent retry. */
        }
      }
      if (e instanceof AssistantError && e.code === "ACCESS_CHANGED") {
        pending.current = null;
        setSession(
          (s) =>
            s && {
              ...s,
              processing: false,
              retry: null,
              state: { ...s.state, access_changed: true },
            },
        );
      }
      return false;
    } finally {
      stopReply();
      if (controller.current === abort) controller.current = null;
      if (mounted.current && !abort.signal.aborted) {
        busy.current = false;
        setLoading(false);
        setSearching(false);
      }
    }
  };
  const clearSession = () => {
    // Deleting the selected consultation also cancels a pending restore/stream.
    controller.current?.abort();
    controller.current = null;
    busy.current = false;
    setLoading(false);
    setSearching(false);
    setComposerKey((key) => key + 1);
    pending.current = null;
    current.current = null;
    setSession(null);
    setError(null);
    createId.current = crypto.randomUUID();
    setParams({}, { replace: true });
  };
  const reset = () => {
    if (busy.current) return;
    clearSession();
  };
  const restore = (id: string) => {
    if (busy.current || deleting.current.has(id)) return;
    if (id === current.current?.id) return;
    setComposerKey((key) => key + 1);
    setRestoreVersion((v) => v + 1);
    pending.current = null;
    current.current = null;
    setSession(null);
    setParams({ session: id }, { replace: true });
  };
  const hide = async (id: string) => {
    if (deleting.current.has(id)) return;
    deleting.current.add(id);
    try {
      await assistantApi.hide(id);
      if (!mounted.current) return;
      setHistory((items) => items.filter((item) => item.id !== id));
      if (id === selectedId.current || id === current.current?.id)
        clearSession();
      await refreshHistory();
    } catch {
      if (mounted.current)
        setError("상담을 삭제하지 못했습니다. 다시 시도해 주세요.");
    } finally {
      deleting.current.delete(id);
    }
  };
  return {
    draftVersion,
    composerKey: String(composerKey),
    session,
    setSession,
    history,
    loading: loading || !!session?.processing,
    error,
    setError,
    historyError,
    refreshHistory,
    historyMonths,
    inputMaxBytes,
    canRetry: !!pending.current,
    searching,
    send,
    reset,
    restore,
    hide,
    retry: () => {
      if (pending.current) void send(pending.current.message);
    },
  };
}
